/**
 * ASCII Art Generator - 鼠标交互计算
 *
 * 实现 attract/push 交互模式，计算字符偏移量
 */

import type { FullRenderSettings } from '../types'
import type { MouseState } from './types'

/**
 * 鼠标交互模式计算结果
 */
export interface MouseInteractionResult {
  /** X 方向偏移（像素） */
  offsetX: number
  /** Y 方向偏移（像素） */
  offsetY: number
  /** 影响强度因子 (0-1) */
  influence: number
}

/**
 * 计算鼠标交互偏移量
 *
 * @param charX 字符 X 坐标
 * @param charY 字符 Y 坐标
 * @param mouseState 鼠标状态
 * @param settings 渲染设置
 * @returns 交互结果
 */
export function calculateMouseOffset(
  charX: number,
  charY: number,
  mouseState: MouseState,
  settings: FullRenderSettings
): MouseInteractionResult {
  // 默认结果：无偏移
  const defaultResult = { offsetX: 0, offsetY: 0, influence: 0 }

  // 检查交互是否启用（兼容ditherMouseInteractionEnabled和dither2MouseInteractionEnabled）
  if (!settings.ditherMouseInteractionEnabled && !settings.dither2MouseInteractionEnabled) {
    return defaultResult
  }

  // 检查鼠标是否在 canvas 内
  if (!mouseState.isInCanvas) {
    return defaultResult
  }

  // 计算距离
  const dx = mouseState.x - charX
  const dy = mouseState.y - charY
  const distance = Math.sqrt(dx * dx + dy * dy)

  // 计算影响半径（基于设置参数）
  // ditherMouseRadius: 0-1.5 范围，转换为像素
  // 使用 canvas 尺寸的百分比计算
  const maxRadius = settings.mouseAreaSize * settings.ditherMouseRadius

  // 超出范围则无影响
  if (distance > maxRadius || distance === 0) {
    return defaultResult
  }

  // 计算衰减因子：距离越近影响越大
  // 使用指数衰减，让边缘更平滑
  const rawDecay = 1 - (distance / maxRadius)
  const decayFactor = Math.pow(rawDecay, settings.mouseSpread)

  // 计算方向向量（归一化）
  const normalizedDx = dx / distance
  const normalizedDy = dy / distance

  // 计算偏移强度
  const strength = settings.hoverStrength * decayFactor

  // 根据交互模式计算偏移方向
  let offsetX: number
  let offsetY: number

  if (settings.mouseInteractionMode === 'attract') {
    // 吸引模式：字符向鼠标位置偏移
    offsetX = normalizedDx * strength
    offsetY = normalizedDy * strength
  } else {
    // 推开模式：字符远离鼠标位置
    offsetX = -normalizedDx * strength
    offsetY = -normalizedDy * strength
  }

  return {
    offsetX,
    offsetY,
    influence: decayFactor
  }
}

/**
 * 鼠标交互类
 *
 * 提供鼠标交互的状态管理和计算
 */
export class MouseInteraction {
  private enabled: boolean
  private mode: 'attract' | 'push'
  private radius: number
  private strength: number
  private spread: number

  constructor(
    mode: 'attract' | 'push' = 'attract',
    radius: number = 0.3,
    strength: number = 24,
    spread: number = 1
  ) {
    this.enabled = true
    this.mode = mode
    this.radius = radius
    this.strength = strength
    this.spread = spread
  }

  /**
   * 计算指定位置的交互偏移
   */
  calculate(
    charX: number,
    charY: number,
    mouseState: MouseState,
    canvasWidth: number,
    canvasHeight: number
  ): MouseInteractionResult {
    if (!this.enabled || !mouseState.isInCanvas) {
      return { offsetX: 0, offsetY: 0, influence: 0 }
    }

    const dx = mouseState.x - charX
    const dy = mouseState.y - charY
    const distance = Math.sqrt(dx * dx + dy * dy)

    // 计算影响半径
    const baseSize = Math.min(canvasWidth, canvasHeight)
    const maxRadius = baseSize * this.radius

    if (distance > maxRadius || distance === 0) {
      return { offsetX: 0, offsetY: 0, influence: 0 }
    }

    // 计算衰减
    const rawDecay = 1 - (distance / maxRadius)
    const decayFactor = Math.pow(rawDecay, this.spread)

    // 方向向量
    const normalizedDx = dx / distance
    const normalizedDy = dy / distance

    // 偏移强度
    const offsetStrength = this.strength * decayFactor

    // 根据模式计算偏移
    const offsetX = this.mode === 'attract'
      ? normalizedDx * offsetStrength
      : -normalizedDx * offsetStrength

    const offsetY = this.mode === 'attract'
      ? normalizedDy * offsetStrength
      : -normalizedDy * offsetStrength

    return { offsetX, offsetY, influence: decayFactor }
  }

  /**
   * 设置交互模式
   */
  setMode(mode: 'attract' | 'push'): void {
    this.mode = mode
  }

  /**
   * 设置影响半径
   */
  setRadius(radius: number): void {
    this.radius = radius
  }

  /**
   * 设置偏移强度
   */
  setStrength(strength: number): void {
    this.strength = strength
  }

  /**
   * 设置衰减曲线
   */
  setSpread(spread: number): void {
    this.spread = spread
  }

  /**
   * 启用/禁用交互
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled
  }

  /**
   * 获取当前状态
   */
  isEnabled(): boolean {
    return this.enabled
  }

  getMode(): 'attract' | 'push' {
    return this.mode
  }
}

/**
 * 计算鼠标交互（简化版本，直接使用设置）
 */
export function calculateMouseInteraction(
  charX: number,
  charY: number,
  mouseState: MouseState,
  settings: FullRenderSettings
): MouseInteractionResult {
  return calculateMouseOffset(charX, charY, mouseState, settings)
}